import auth from '@/auth/auth';
import { useMutation } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { DocumentNode } from 'graphql';
import { computed, Ref, toValue } from 'vue';
import { BadgeDropDownStatus, BadgeDropDownValue, BadgeDropDownVariant } from './BadgeDropDown.vue';

export function useBadgeDropDown(
    status: Ref<string>,
    objectId: Ref<string>,
    updateDocument: DocumentNode,
    getStatusLabel: (status: string) => string,
    getBadgeVariant: (status: string) => BadgeDropDownVariant,
    options: string[],
) {
    const fluent = useFluent();
    const isAdmin = auth.isAdmin();

    const updateMutation = useMutation(updateDocument);

    const statusBadgeDisabledExplanation = computed(() => {
        return isAdmin ? undefined : fluent.$t('no-permission-to-edit');
    });

    function getStatusBadgeCurrentValue(): BadgeDropDownValue {
        return {
            id: toValue(status),
            label: getStatusLabel(toValue(status)),
            variant: getBadgeVariant(toValue(status)),
        };
    }

    function getStatusBadgeItems(): Array<BadgeDropDownValue & { isSelected: boolean }> {
        return options.map((s) => ({
            id: s,
            label: getStatusLabel(s),
            variant: getBadgeVariant(s),
            isSelected: toValue(status) === s,
        }));
    }

    function getStatusBadgeStatus(): BadgeDropDownStatus {
        if (statusBadgeDisabledExplanation.value) return 'Disabled';
        if (updateMutation.loading.value) return 'Loading';
        if (updateMutation.error.value) return 'Retry';
        return 'Default';
    }

    async function handleStatusBadgeChange(newStatus: string) {
        const response = await updateMutation.mutate({
            id: toValue(objectId),
            status: newStatus,
        });
        if (response?.errors) {
            throw new Error(`Could not update dropdown value: ${response.errors}`);
        }
        if (!response?.data) {
            throw new Error('Update dropdown value did not return data.');
        }
        return response.data;
    }

    return {
        statusBadgeDisabledExplanation,
        getStatusBadgeCurrentValue,
        getStatusBadgeItems,
        getStatusBadgeStatus,
        handleStatusBadgeChange,
    };
}
